// Sign-in audit (F-ADMIN §5). Every successful entry into the app writes one
// app_logins row: WHO, WHEN, from what device, and whether it was a fresh
// LOGIN (credentials typed) or a RESTORE (an existing session resumed on page
// load). RLS: any signed-in user may insert their OWN row; only admins read.
//
// Logging is BEST-EFFORT by design: a nurse must never be locked out of
// patient care because an audit insert failed (offline retry, missing table
// before the migration runs, RLS drift). Failures surface in the device error
// log via the caller's envelope, never as a blocking dialog.

import { getClient, ok, fail } from '../db/client.js';
import { TABLES } from '../db/contract.js';

/**
 * Record a sign-in. Never throws; returns the house envelope.
 * @param {'login'|'restore'} kind fresh credentials vs restored session
 * @param {{id: string, email?: string}|null} user the authenticated user
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function logSignIn(kind, user, deps) {
  try {
    if (!user || !user.id) return fail(new Error('no user'));
    const client = (deps && deps.client) || getClient();
    const nav = /** @type {any} */ (globalThis).navigator;
    const { error } = await client.from(TABLES.APP_LOGINS).insert([
      {
        user_id: user.id,
        email: user.email || null,
        kind: kind === 'restore' ? 'restore' : 'login',
        user_agent: nav && nav.userAgent ? String(nav.userAgent).slice(0, 250) : null,
      },
    ]);
    if (error) return fail(error);
    return ok(true);
  } catch (e) {
    return fail(e);
  }
}

/**
 * Load recent sign-ins (admin-only by RLS — non-admins get an empty read or a
 * policy error; both land as the envelope, never a throw). Newest first.
 * @param {{client?: any, limit?: number}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadSignIns(deps) {
  try {
    const client = (deps && deps.client) || getClient();
    const { data, error } = await client
      .from(TABLES.APP_LOGINS)
      .select('*')
      .order('logged_at', { ascending: false })
      .limit((deps && deps.limit) || 200);
    if (error) return fail(error);
    return ok(data || []);
  } catch (e) {
    return fail(e);
  }
}

/**
 * Pure: per-user sign-in totals from a loaded list — "all the times they have
 * logged on" at a glance. Fresh logins and restores are counted separately so
 * a left-open clinic tab does not inflate someone's apparent activity.
 * @param {any[]} rows app_logins rows
 * @returns {Array<{user_id: string, email: string, logins: number, restores: number, last: string}>} sorted by last desc
 */
/** Local (not UTC) YYYY-MM-DD. The clinic reads its own clock, not Greenwich's. */
function dayKey(d) {
  const p = (/** @type {number} */ x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Pure: sign-ins per day across a trailing window — the shape of when the team
 * actually works. Every day in the window is present INCLUDING quiet ones, so a
 * weekend or an outage reads as a visible gap instead of collapsing out of the
 * chart and making the run look continuous.
 * @param {any[]} rows app_logins rows
 * @param {number} days window length, ending today; anything not a positive
 *   number (0, negative, NaN, missing) falls back to 14 rather than producing a
 *   one-day or empty axis, which would read as "the team barely signs in".
 * @param {Date} [now] injectable clock
 * @returns {Array<{key: string, label: string, logins: number, restores: number, n: number}>} oldest first
 */
export function signInDays(rows, days, now) {
  const n = Number.isFinite(days) && days > 0 ? Math.floor(days) : 14;
  const base = now instanceof Date && !isNaN(now.getTime()) ? new Date(now.getTime()) : new Date();
  base.setHours(0, 0, 0, 0);
  /** @type {Record<string, any>} */
  const idx = {};
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(base.getTime());
    d.setDate(d.getDate() - i);
    const e = { key: dayKey(d), label: `${d.getDate()} ${MONTHS[d.getMonth()]}`, logins: 0, restores: 0, n: 0 };
    idx[e.key] = e;
    out.push(e);
  }
  for (const r of rows || []) {
    if (!r || !r.logged_at) continue;
    const t = new Date(r.logged_at);
    if (isNaN(t.getTime())) continue;
    const e = idx[dayKey(t)];
    if (!e) continue; // outside the window
    if (r.kind === 'restore') e.restores++;
    else e.logins++;
    e.n++;
  }
  return out;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Pure: the adoption question in one object — "has anyone actually been in?"
 *
 * The panels below this answer "who signed in and when", which is the right
 * question once the app is in use and the wrong one when it is not: a wall of
 * per-person rows reading zero takes a while to add up to "nobody came". This
 * collapses the window into the four numbers that decide whether there is an
 * adoption problem at all.
 *
 * `people` counts DISTINCT humans, not entries, and a restored session counts:
 * for "did anyone open the app this week" a resumed tab is still a person
 * looking at the screen. The login/restore split stays visible in `entries` vs
 * `logins` so a single tab left open all week can still be told apart from
 * seven mornings of work.
 *
 * @param {any[]} rows app_logins rows
 * @param {number} days trailing window; non-positive or missing falls back to 7
 * @param {Date} [now] injectable clock
 * @returns {{people: number, entries: number, logins: number, activeDays: number, last: string|null, since: string}}
 */
export function adoption(rows, days, now) {
  const n = Number.isFinite(days) && days > 0 ? Math.floor(days) : 7;
  const base = now instanceof Date && !isNaN(now.getTime()) ? new Date(now.getTime()) : new Date();
  const since = new Date(base.getTime());
  since.setHours(0, 0, 0, 0);
  since.setDate(since.getDate() - (n - 1));
  const people = new Set();
  const activeDays = new Set();
  let entries = 0;
  let logins = 0;
  /** @type {string|null} */
  let last = null;
  for (const r of rows || []) {
    if (!r || !r.logged_at) continue;
    const t = new Date(r.logged_at);
    if (isNaN(t.getTime())) continue;
    if (!last || String(r.logged_at) > last) last = String(r.logged_at); // newest overall, window or not
    if (t < since) continue;
    entries++;
    if (r.kind !== 'restore') logins++;
    if (r.user_id) people.add(r.user_id);
    activeDays.add(dayKey(t));
  }
  return { people: people.size, entries, logins, activeDays: activeDays.size, last, since: since.toISOString() };
}

export function signInTotals(rows) {
  /** @type {Record<string, {user_id: string, email: string, logins: number, restores: number, last: string}>} */
  const by = {};
  for (const r of rows || []) {
    if (!r || !r.user_id) continue;
    const t = by[r.user_id] || (by[r.user_id] = { user_id: r.user_id, email: r.email || '', logins: 0, restores: 0, last: '' });
    if (r.kind === 'restore') t.restores++;
    else t.logins++;
    if (!t.email && r.email) t.email = r.email;
    if (!t.last || String(r.logged_at) > t.last) t.last = String(r.logged_at);
  }
  return Object.values(by).sort((a, b) => (a.last < b.last ? 1 : -1));
}
